import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { GroupSelect } from "@/components/GroupSelect";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent";
const labelCls = "block text-sm font-semibold text-gray-700 mb-1";

export function UserFormDialog({ open, onOpenChange, userRecord, groups, onSaved }) {
  const isEdit = Boolean(userRecord);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [title, setTitle] = useState("");
  const [phone, setPhone] = useState("");
  const [grantLogin, setGrantLogin] = useState(true);
  const [groupIds, setGroupIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);

  useEffect(() => {
    if (!open) { setInviteLink(null); return; }
    setName(userRecord?.name || "");
    setEmail(userRecord?.email || "");
    setRole(userRecord?.role || "user");
    setTitle(userRecord?.title || "");
    setPhone(userRecord?.phone || "");
    setGrantLogin(true);
    setGroupIds(userRecord?.group_ids || []);
    setPassword("");
  }, [open, userRecord]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/users/${userRecord.id}`, { name, role, group_ids: groupIds, title, phone });
        toast.success("Employee updated");
        onOpenChange(false);
      } else {
        const { data } = await api.post("/users", { name, email, role, group_ids: groupIds, title, phone, grant_login: grantLogin });
        onSaved?.();
        if (!grantLogin) {
          toast.success("Employee added to directory (no login access)");
          onOpenChange(false);
          return;
        }
        if (data.invite?.sent) {
          toast.success("Employee created — invite email sent");
          onOpenChange(false);
        } else {
          setInviteLink(data.invite?.link || null);
          toast.message("Employee created. Share the invite link below.");
        }
        return;
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save employee");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-sm" data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight">{isEdit ? "Edit employee" : "New employee"}</DialogTitle>
          <DialogDescription className="sr-only">Fill in the employee details and team access.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1">
          <div>
            <label className={labelCls}>Full name</label>
            <input data-testid="user-name-input" required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Job title</label>
              <input data-testid="user-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Paralegal" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input data-testid="user-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input data-testid="user-email-input" type="email" required disabled={isEdit} value={email} onChange={(e) => setEmail(e.target.value)}
              className={`${inputCls} ${isEdit ? "bg-gray-100 text-gray-500" : ""}`} />
          </div>
          {!isEdit && (
            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50" data-testid="grant-login-toggle">
              <input type="checkbox" checked={grantLogin} onChange={(e) => setGrantLogin(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#002FA7]" data-testid="grant-login-checkbox" />
              <span className="text-sm">
                <span className="font-semibold text-gray-800">Grant login access</span>
                <span className="block text-xs text-gray-500">Sends an invite so they can sign in. Uncheck to add a directory-only employee (assignable to cases, but cannot log in).</span>
              </span>
            </label>
          )}
          {!isEdit && grantLogin && !inviteLink && (
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-[#002FA7]">
              An invite email with a secure “set your password” link will be sent to this address. If email delivery isn’t configured, the link will be shown here to share manually.
            </div>
          )}
          {inviteLink && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="invite-link-box">
              <p className="text-xs font-semibold text-amber-800 mb-1">Email not sent — share this invite link:</p>
              <div className="flex gap-2">
                <input readOnly value={inviteLink} className={`${inputCls} text-xs`} data-testid="invite-link-input" />
                <button type="button" onClick={() => { navigator.clipboard?.writeText(inviteLink); toast.success("Copied"); }}
                  className="bg-[#002FA7] text-white px-3 rounded-sm text-xs font-semibold shrink-0">Copy</button>
              </div>
            </div>
          )}
          <div>
            <label className={labelCls}>Role</label>
            <select data-testid="user-role-select" value={role} onChange={(e) => setRole(e.target.value)} className={inputCls}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Teams <span className="font-normal text-gray-400">(controls which clients they can access)</span></label>
            <GroupSelect groups={groups} selected={groupIds} onChange={setGroupIds} testidPrefix="user-group" />
          </div>
          <DialogFooter className="pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">{inviteLink ? "Done" : "Cancel"}</button>
            {!inviteLink && (
              <button type="submit" disabled={saving} data-testid="user-save-button"
                className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} {isEdit ? "Save changes" : (grantLogin ? "Create & invite" : "Add employee")}
              </button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
